import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Card, CardContent, Stack, Chip, IconButton,
  Button, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Snackbar, Alert,
  Divider, Tooltip, Badge, Autocomplete, CircularProgress, Collapse,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EventIcon from "@mui/icons-material/Event";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import PhoneIcon from "@mui/icons-material/Phone";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import GroupIcon from "@mui/icons-material/Group";
import TodayIcon from "@mui/icons-material/Today";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PremiumDateTimePicker from "../../Components/PremiumDateTimePicker";
import {
  fetchMyTasks, createTask, updateTask, markLeadContacted,
  MyTasksResponse, Task, UncontactedLead, PRIORITY_CONFIG, TaskScope,
} from "./taskApi";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import { fetchLeads, fetchTeamUsers, type LeadThread, type TeamUser } from "../LeadSystem/leadApi";
import TaskDetailDialog from "./TaskDetailDialog";

const NAVY = "#0d1b2a";
const GOLD = "#c9a84c";

/* ── Helpers ────────────────────────────────────────────────────────── */
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });

const fmtRelative = (iso: string) => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const diffMs = Date.now() - t;
  const abs    = Math.abs(diffMs);
  const min    = Math.round(abs / 60_000);
  if (min < 1)   return diffMs >= 0 ? "just now"   : "in a moment";
  if (min < 60)  return diffMs >= 0 ? `${min}m ago` : `in ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24)   return diffMs >= 0 ? `${hr}h ago`  : `in ${hr}h`;
  const d = Math.round(hr / 24);
  return diffMs >= 0 ? `${d}d ago` : `in ${d}d`;
};

/* Display name for a user-shaped object. Order: display_name > first+last >
   username > "Unassigned". Used by Team-scope cards to label "Assigned to X"
   without each call-site repeating the same join. */
const userDisplayName = (u: any): string => {
  if (!u) return "Unassigned";
  const full = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return u.display_name || full || u.username || "Unassigned";
};

const userInitials = (u: any): string => {
  if (!u) return "?";
  const f = (u.first_name?.[0] ?? "").toUpperCase();
  const l = (u.last_name?.[0]  ?? "").toUpperCase();
  if (f || l) return `${f}${l}`;
  return (u.username?.[0] ?? "?").toUpperCase();
};

/* Outcome chip lookup for the TaskCard last-attempt badge.
   Compact subset of FOLLOW_UP_OUTCOME_OPTIONS — kept here so the card
   doesn't have to import the full options array. */
const LAST_ATTEMPT_META: Record<string, { label: string; color: string; icon?: React.ReactNode }> = {
  contacted:          { label: "Contacted",          color: "#16a34a" },
  no_answer:          { label: "No Answer",          color: "#f59e0b" },
  voicemail:          { label: "Voicemail",          color: "#3b82f6" },
  interested:         { label: "Interested",         color: "#0d9488" },
  not_interested:     { label: "Not Interested",     color: "#ef4444" },
  callback_requested: { label: "Callback Requested", color: "#8b5cf6" },
  email_sent:         { label: "Email Sent",         color: "#0ea5e9" },
  meeting_scheduled:  { label: "Meeting Scheduled",  color: "#22c55e" },
};
const TASKCARD_TYPE_META: Record<string, { label: string; color: string; icon?: React.ReactNode }> = {
  call:    { label: "Call",    color: "#3b82f6" },
  email:   { label: "Email",   color: "#8b5cf6" },
  meeting: { label: "Meeting", color: "#10b981" },
  note:    { label: "Note",    color: "#f59e0b" },
};

/* AI Score badge */
const AiScore = ({ score }: { score: string | null }) => {
  if (!score) return null;
  const lower = score.toLowerCase();
  if (lower === "hot")  return <Chip label="Hot"  icon={<LocalFireDepartmentIcon sx={{ fontSize: "12px !important" }} />} size="small" sx={{ bgcolor: "#ff5252", color: "#fff", height: 20, fontSize: 10, fontWeight: 700 }} />;
  if (lower === "warm") return <Chip label="Warm" icon={<WhatshotIcon            sx={{ fontSize: "12px !important" }} />} size="small" sx={{ bgcolor: "#ff9800", color: "#fff", height: 20, fontSize: 10, fontWeight: 700 }} />;
  return <Chip label="Cold" icon={<AcUnitIcon sx={{ fontSize: "12px !important" }} />} size="small" sx={{ bgcolor: "#42a5f5", color: "#fff", height: 20, fontSize: 10, fontWeight: 700 }} />;
};

/* Lead status chip */
const LEAD_STATUS_COLOR: Record<string, string> = {
  new: "#3b82f6", contacted: "#8b5cf6", negotiation: "#f59e0b",
  ready_for_quote: "#14b8a6", ready_for_booking: "#22c55e", confirmed: "#16a34a", lost: "#ef4444",
};
const LeadStatusBadge = ({ status }: { status: string }) => (
  <Chip
    label={status.replace(/_/g, " ")}
    size="small"
    sx={{
      height: 18, fontSize: 10, fontWeight: 700, textTransform: "capitalize",
      bgcolor: `${LEAD_STATUS_COLOR[status] ?? "#9e9e9e"}18`,
      color: LEAD_STATUS_COLOR[status] ?? "#9e9e9e",
      border: `1px solid ${LEAD_STATUS_COLOR[status] ?? "#9e9e9e"}30`,
    }}
  />
);

/* ── KPI card ────────────────────────────────────────────────────────── */
interface KpiProps {
  label: string; value: number; icon: React.ReactNode;
  color: string; urgent?: boolean; onClick?: () => void;
}
const KpiCard = ({ label, value, icon, color, urgent, onClick }: KpiProps) => (
  <Card
    elevation={0}
    onClick={onClick}
    sx={{
      flex: 1, minWidth: 100, border: urgent ? `1.5px solid ${color}` : "1px solid #e8e8e8",
      borderRadius: 2, bgcolor: urgent && value > 0 ? `${color}08` : "#fff",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.15s",
      "&:hover": onClick ? { boxShadow: "0 2px 10px rgba(0,0,0,0.08)", borderColor: color } : {},
    }}
  >
    <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: `${color}15`, color }}>{icon}</Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} color={urgent && value > 0 ? color : NAVY} lineHeight={1}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{label}</Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

/* ── Uncontacted lead row ─────────────────────────────────────────────── */
const UncontactedRow = ({
  lead, onContacted, onView, showAssignee,
}: { lead: UncontactedLead; onContacted: (id: number) => void; onView: (id: number) => void; showAssignee?: boolean }) => {
  const [marking, setMarking] = useState(false);
  const age = Math.floor((Date.now() - new Date(lead.created_at).getTime()) / 3600000);
  const isOld = age >= 24;

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${isOld ? "#fecaca" : "#fee2e2"}`,
        bgcolor: isOld ? "#fff1f2" : "#fff5f5",
        borderRadius: 1.5, mb: 1,
      }}
    >
      <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap">
              <Typography variant="body2" fontWeight={700} color={NAVY} noWrap>
                {lead.name}
              </Typography>
              <AiScore score={lead.ai_score} />
              {isOld && (
                <Chip label="Overdue 24h+" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#ef4444", color: "#fff" }} />
              )}
            </Stack>
            <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 0.3 }} flexWrap="wrap">
              {lead.event_type && (
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                  {lead.event_type}{lead.guest_count ? ` · ${lead.guest_count} guests` : ""}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: isOld ? "#ef4444" : "#f97316", fontWeight: 600 }}>
                {fmtRelative(lead.created_at)}
              </Typography>
              {showAssignee && (
                <Chip
                  size="small"
                  label={userDisplayName(lead.assignee)}
                  sx={{
                    height: 18, fontSize: 10, fontWeight: 700,
                    bgcolor: "#f1f5f9", color: NAVY, border: "1px solid #e2e8f0",
                  }}
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" gap={0.5} alignItems="center">
            <Tooltip title="Call">
              <IconButton size="small" sx={{ color: "#3b82f6" }}>
                <PhoneIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="View lead">
              <IconButton size="small" onClick={() => onView(lead.id)} sx={{ color: "#6b7280" }}>
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              disabled={marking}
              onClick={async () => {
                setMarking(true);
                await onContacted(lead.id);
                setMarking(false);
              }}
              sx={{
                bgcolor: "#10b981", color: "#fff", fontWeight: 700, fontSize: 11,
                borderRadius: 1.5, textTransform: "none", px: 1.2, py: 0.4, minWidth: 0,
                "&:hover": { bgcolor: "#059669" },
              }}
            >
              {marking ? <CircularProgress size={12} color="inherit" /> : "Mark Contacted"}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

/* ── "Due in X" countdown — amber tier when ≤ 60 min, red when past ──── */
function dueInLabel(iso: string): { text: string; color: string } | null {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  const absMin = Math.round(Math.abs(diffMs) / 60_000);
  const past   = diffMs < 0;
  let text: string;
  if (absMin < 1)            text = past ? "just past due" : "due now";
  else if (absMin < 60)      text = `${past ? "" : "in "}${absMin} min${past ? " overdue" : ""}`;
  else if (absMin < 60 * 24) text = `${past ? "" : "in "}${Math.round(absMin / 60)}h${past ? " overdue" : ""}`;
  else                        text = `${past ? "" : "in "}${Math.round(absMin / (60 * 24))}d${past ? " overdue" : ""}`;
  const color = past ? "#dc2626" : (absMin <= 60 ? "#b45309" : "#64748b");
  return { text, color };
}

/* ── Task card ───────────────────────────────────────────────────────── */
const TaskCard = ({
  task, onComplete, isOverdue, onMarkLeadContacted, onOpenDetail, showAssignee,
}: {
  task: Task; onComplete: (id: number) => void;
  isOverdue?: boolean; onMarkLeadContacted?: (leadId: number) => void;
  onOpenDetail?: (task: Task) => void;
  /** Team view: show "Assigned to X" chip so admin can scan ownership. */
  showAssignee?: boolean;
}) => {
  const navigate = useNavigate();
  const [completing, setCompleting] = useState(false);
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const lead = task.lead as any;

  const borderColor = isOverdue ? "#fecaca" : "#e8e8e8";
  const bgColor = isOverdue ? "#fef2f2" : "#fff";

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2, border: `1px solid ${borderColor}`, bgcolor: bgColor,
        "&:hover": { borderColor: GOLD, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
        transition: "all 0.15s",
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" alignItems="flex-start" gap={1.5}>
          {/* Complete button */}
          <Tooltip title="Mark complete">
            <IconButton
              size="small"
              disabled={completing}
              onClick={async () => {
                setCompleting(true);
                await onComplete(task.id);
              }}
              sx={{
                border: `2px solid ${isOverdue ? "#fca5a5" : "#d1d5db"}`,
                width: 28, height: 28, mt: 0.2, flexShrink: 0,
                "&:hover": { borderColor: "#10b981", bgcolor: "#ecfdf5" },
              }}
            >
              {completing
                ? <CircularProgress size={14} />
                : <CheckCircleOutlineIcon sx={{ fontSize: 16, color: isOverdue ? "#fca5a5" : "#d1d5db" }} />}
            </IconButton>
          </Tooltip>

          {/* Content */}
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={700} color={NAVY} sx={{ lineHeight: 1.3 }}>
              {task.title}
            </Typography>

            {/* Lead info */}
            {lead && (
              <Stack direction="row" gap={0.6} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                <LeadStatusBadge status={lead.status ?? "new"} />
                {lead.ai_score && <AiScore score={lead.ai_score} />}
                {lead.event_type && (
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                    {lead.event_type}{lead.guest_count ? ` · ${lead.guest_count}` : ""}
                  </Typography>
                )}
                {/* Team view: surface the task's assignee inline with the
                    lead facts so the admin can see at a glance whose
                    queue this is on. */}
                {showAssignee && (
                  <Chip
                    size="small"
                    avatar={<Avatar sx={{ bgcolor: NAVY, color: "#fff", fontSize: 9, fontWeight: 800 }}>{userInitials(task.assignee)}</Avatar>}
                    label={userDisplayName(task.assignee)}
                    sx={{
                      height: 20, fontSize: 10, fontWeight: 700,
                      bgcolor: "#f1f5f9", color: NAVY, border: "1px solid #e2e8f0",
                      "& .MuiChip-avatar": { width: 16, height: 16 },
                    }}
                  />
                )}
              </Stack>
            )}

            {/* Follow-up thread preview — last attempt + counters.
                Surfaces operational state without opening the dialog. */}
            {(task.last_attempt || (task.follow_up_attempts ?? 0) > 0) && (
              <Stack direction="row" gap={0.6} alignItems="center" sx={{ mt: 0.6 }} flexWrap="wrap">
                {task.last_attempt && (() => {
                  const outcome = task.last_attempt.follow_up_outcome ?? task.last_attempt.outcome;
                  const om = LAST_ATTEMPT_META[outcome ?? ""] ?? null;
                  const tm = TASKCARD_TYPE_META[task.last_attempt.type] ?? null;
                  return (
                    <Chip
                      size="small"
                      icon={(om?.icon ?? tm?.icon) as any}
                      label={
                        outcome
                          ? `Last: ${tm?.label ?? task.last_attempt.type} · ${om?.label ?? outcome.replace(/_/g, " ")}`
                          : `Last: ${tm?.label ?? task.last_attempt.type}`
                      }
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 700,
                        bgcolor: `${(om?.color ?? tm?.color ?? "#64748b")}15`,
                        color:   (om?.color ?? tm?.color ?? "#64748b"),
                        border: `1px solid ${(om?.color ?? tm?.color ?? "#64748b")}35`,
                        "& .MuiChip-icon": { color: `${(om?.color ?? tm?.color ?? "#64748b")} !important`, fontSize: "11px !important" },
                      }}
                    />
                  );
                })()}
                {(task.follow_up_attempts ?? 0) > 0 && (
                  <Chip
                    size="small"
                    label={`${task.follow_up_attempts} attempt${task.follow_up_attempts === 1 ? "" : "s"}`}
                    sx={{
                      height: 18, fontSize: 9.5, fontWeight: 700,
                      bgcolor: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe",
                    }}
                  />
                )}
                {task.last_activity_at && (
                  <Tooltip title={new Date(task.last_activity_at).toLocaleString()}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontWeight: 600 }}>
                      Updated {fmtRelative(task.last_activity_at)}
                    </Typography>
                  </Tooltip>
                )}
              </Stack>
            )}

            {task.notes && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.3, display: "block" }} noWrap>
                {task.notes}
              </Typography>
            )}
          </Box>

          {/* Right side */}
          <Stack alignItems="flex-end" gap={0.4} flexShrink={0}>
            <Stack direction="row" alignItems="center" gap={0.4}>
              <AccessTimeIcon sx={{ fontSize: 12, color: isOverdue ? "#ef4444" : "#94a3b8" }} />
              <Typography variant="caption" fontWeight={700} color={isOverdue ? "#ef4444" : "text.secondary"}>
                {fmtTime(task.due_at)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {fmtDate(task.due_at)}
            </Typography>
            {(() => {
              const due = dueInLabel(task.due_at);
              return due ? (
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: 10, color: due.color }}>
                  {due.text}
                </Typography>
              ) : null;
            })()}
            <Chip
              label={priority.label}
              size="small"
              sx={{
                height: 18, fontSize: 10, fontWeight: 700,
                bgcolor: `${priority.color}18`, color: priority.color,
                border: `1px solid ${priority.color}30`,
              }}
            />

            {/* Action row */}
            <Stack direction="row" gap={0.3} alignItems="center" sx={{ mt: 0.3 }}>
              {onOpenDetail && (
                <Tooltip title="Open task (reschedule · reassign · cancel · in-progress)">
                  <IconButton size="small" onClick={() => onOpenDetail(task)}
                    sx={{ p: 0.3, color: NAVY, "&:hover": { color: GOLD } }}>
                    <OpenInFullIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              {lead && (
                <>
                  <Tooltip title="Open lead">
                    <IconButton size="small" onClick={() => navigate(`/leads/${task.lead_id}`)}
                      sx={{ p: 0.3, color: "#6b7280" }}>
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                  {lead.status === "new" && onMarkLeadContacted && (
                    <Tooltip title="Mark lead as contacted">
                      <IconButton
                        size="small"
                        onClick={() => onMarkLeadContacted(task.lead_id!)}
                        sx={{ p: 0.3, color: "#10b981" }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Call">
                    <IconButton size="small" sx={{ p: 0.3, color: "#3b82f6" }}>
                      <PhoneIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

/* ── Section header ──────────────────────────────────────────────────── */
const SectionHeader = ({
  icon, title, count, color, expanded, onToggle,
}: {
  icon: React.ReactNode; title: string; count: number; color: string;
  expanded: boolean; onToggle: () => void;
}) => (
  <Stack
    direction="row" alignItems="center" gap={1} mb={1.5} mt={2.5}
    onClick={onToggle}
    sx={{ cursor: "pointer", userSelect: "none" }}
  >
    {icon}
    <Typography variant="subtitle2" fontWeight={700} color={NAVY} flex={1}>{title}</Typography>
    <Box
      sx={{
        bgcolor: color, color: "#fff", borderRadius: 10, px: 1, py: 0.1,
        fontSize: 11, fontWeight: 700, minWidth: 22, textAlign: "center",
      }}
    >
      {count}
    </Box>
    {expanded ? <ExpandLessIcon sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
  </Stack>
);

/* ═══════════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                      */
/* ═══════════════════════════════════════════════════════════════════ */

const SCOPE_STORAGE_KEY = "tasks.scope";

const MyTasks: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<MyTasksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  /* Scope toggle — persisted across reloads so the operator's last choice
     sticks. Server silently demotes 'team' for non-privileged users, so
     a stale 'team' value from a downgraded account harmlessly returns to
     the 'my' scope on next load (we re-sync from response.scope below). */
  const [scope, setScope] = useState<TaskScope>(() =>
    (typeof window !== "undefined" && (localStorage.getItem(SCOPE_STORAGE_KEY) as TaskScope)) || "my"
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [priority, setPriority] = useState("medium");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" as "success" | "error" });

  // Section collapse state
  const [overdueOpen, setOverdueOpen] = useState(true);
  const [todayOpen,   setTodayOpen]   = useState(true);
  const [upcomingOpen, setUpcomingOpen] = useState(true);

  // Lead linking in create dialog
  const [linkedLead, setLinkedLead] = useState<LeadThread | null>(null);
  const [leadSearchInput, setLeadSearchInput] = useState("");
  const [leadOptions, setLeadOptions] = useState<LeadThread[]>([]);
  const [leadSearching, setLeadSearching] = useState(false);

  // Task detail dialog — opens on any task surface ("Open" button).
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [teamUsers, setTeamUsers]   = useState<TeamUser[]>([]);

  // Overdue auto-tick: re-render every 60s so the "isOverdue" check
  // (computed at render time against new Date()) flips without manual
  // reload. Client-side only, no API hit.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { fetchTeamUsers().then(setTeamUsers).catch(() => {}); }, []);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLeadInputChange = (_: any, value: string) => {
    setLeadSearchInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!value.trim()) { setLeadOptions([]); return; }
    searchTimer.current = setTimeout(async () => {
      setLeadSearching(true);
      try { setLeadOptions(await fetchLeads({ search: value, per_page: 10 })); } catch {}
      setLeadSearching(false);
    }, 350);
  };

  const resetDialog = () => {
    setTitle(""); setDueAt(""); setPriority("medium"); setNotes("");
    setLinkedLead(null); setLeadSearchInput(""); setLeadOptions([]);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMyTasks(scope);
      setData(res);
      // Server may have demoted 'team' → 'my' if the user lost privilege.
      // Reconcile local state + storage so the toggle reflects truth.
      if (res?.scope && res.scope !== scope) {
        setScope(res.scope);
        try { localStorage.setItem(SCOPE_STORAGE_KEY, res.scope); } catch {}
      }
    } catch {}
    setLoading(false);
  }, [scope]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleScopeChange = (_: any, next: TaskScope | null) => {
    if (!next || next === scope) return;
    setScope(next);
    try { localStorage.setItem(SCOPE_STORAGE_KEY, next); } catch {}
  };

  const showSnack = (msg: string, severity: "success" | "error" = "success") =>
    setSnack({ open: true, msg, severity });

  const handleComplete = async (id: number) => {
    try {
      await updateTask(id, { status: "completed" });
      showSnack("To-do completed!");
      loadData();
    } catch { showSnack("Failed to complete", "error"); }
  };

  const handleMarkContacted = async (leadId: number) => {
    try {
      await markLeadContacted(leadId);
      showSnack("Lead marked as contacted");
      loadData();
    } catch { showSnack("Failed to update lead", "error"); }
  };

  const handleCreate = async () => {
    if (!title.trim() || !dueAt) return;
    setSaving(true);
    try {
      await createTask({ title, due_at: dueAt, priority, notes: notes || undefined, lead_id: linkedLead?.id });
      showSnack("To-do created");
      setDialogOpen(false);
      resetDialog();
      loadData();
    } catch (e: any) { showSnack(e.message, "error"); }
    setSaving(false);
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
      <CircularProgress />
    </Box>
  );

  const stats = data?.stats ?? { total: 0, overdue: 0, today: 0, upcoming: 0, new_leads: 0, follow_ups_today: 0 };
  const newLeads: UncontactedLead[] = data?.new_leads ?? [];
  const hasUrgentLeads = newLeads.length > 0;
  const canToggleScope = !!data?.can_view_team;
  const inTeamScope    = scope === "team";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f5f6fa", minHeight: "100vh" }}>

      {/* ── HEADER ─────────────────────────────────────────────────── */}
      <Card elevation={0} sx={{ mb: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${NAVY} 0%, #1e3a5f 100%)`, color: "#fff" }}>
        <CardContent sx={{ py: 2.5, px: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <AssignmentIcon sx={{ fontSize: 38, opacity: 0.85 }} />
              <Box>
                <Typography variant="h5" fontWeight={800} lineHeight={1}>
                  {inTeamScope ? "Team To-Do Dashboard" : "My To-Do Dashboard"}
                </Typography>
                <Stack direction="row" gap={2} mt={0.5} flexWrap="wrap">
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    {stats.total} active &middot; {stats.overdue} overdue &middot; {stats.today} today
                  </Typography>
                  {hasUrgentLeads && (
                    <Typography variant="caption" sx={{ color: "#fca5a5", fontWeight: 700 }}>
                      🚨 {newLeads.length} new lead{newLeads.length !== 1 ? "s" : ""} not contacted
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" gap={1} alignItems="center">
              {canToggleScope && (
                <ToggleButtonGroup
                  value={scope}
                  exclusive
                  size="small"
                  onChange={handleScopeChange}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.08)",
                    borderRadius: 2,
                    "& .MuiToggleButton-root": {
                      color: "rgba(255,255,255,0.7)",
                      border: "none",
                      px: 1.5, py: 0.5,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      "&.Mui-selected": { bgcolor: GOLD, color: NAVY, "&:hover": { bgcolor: "#b89540" } },
                    },
                  }}
                >
                  <ToggleButton value="my"><PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />Mine</ToggleButton>
                  <ToggleButton value="team"><GroupsIcon sx={{ fontSize: 16, mr: 0.5 }} />Team</ToggleButton>
                </ToggleButtonGroup>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonSearchIcon />}
                onClick={() => navigate("/leads/inquiries?status=new")}
                sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", textTransform: "none", borderRadius: 2, fontSize: "0.8rem" }}
              >
                View Leads
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
                sx={{ bgcolor: GOLD, color: NAVY, fontWeight: 700, borderRadius: 2, "&:hover": { bgcolor: "#b89540" } }}
              >
                New To-Do
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ── 🚨 CRITICAL ALERT STRIP ──────────────────────────────────── */}
      {hasUrgentLeads && (
        <Card elevation={0} sx={{ mb: 2.5, borderRadius: 2, border: "1.5px solid #ef4444", bgcolor: "#fff5f5" }}>
          <CardContent sx={{ py: 1.5, px: 2.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <NotificationsActiveIcon sx={{ color: "#ef4444", fontSize: 28 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} color="#ef4444">
                    🚨 ACTION REQUIRED
                  </Typography>
                  <Typography variant="body2" color="#b91c1c">
                    You have <strong>{newLeads.length}</strong> new lead{newLeads.length !== 1 ? "s" : ""} that {newLeads.length === 1 ? "has" : "have"} not been contacted
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate("/leads/inquiries?status=new&assigned_to=me")}
                sx={{ bgcolor: "#ef4444", color: "#fff", fontWeight: 700, textTransform: "none", borderRadius: 1.5, "&:hover": { bgcolor: "#dc2626" } }}
              >
                View Leads
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── KPI ROW ──────────────────────────────────────────────────── */}
      <Stack direction="row" gap={1.5} mb={2.5} flexWrap="wrap">
        <KpiCard
          label="Overdue" value={stats.overdue} color="#ef4444" urgent
          icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
          onClick={() => setOverdueOpen(true)}
        />
        <KpiCard
          label="Due Today" value={stats.today} color="#3b82f6"
          icon={<TodayIcon sx={{ fontSize: 18 }} />}
          onClick={() => setTodayOpen(true)}
        />
        <KpiCard
          label="Upcoming" value={stats.upcoming} color="#10b981"
          icon={<HourglassEmptyIcon sx={{ fontSize: 18 }} />}
          onClick={() => setUpcomingOpen(true)}
        />
        <KpiCard
          label="New Leads" value={stats.new_leads} color="#ef4444" urgent
          icon={<PersonSearchIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate("/leads/inquiries?status=new&assigned_to=me")}
        />
        <KpiCard
          label="Follow-ups Today" value={stats.follow_ups_today} color="#f59e0b"
          icon={<GroupIcon sx={{ fontSize: 18 }} />}
        />
      </Stack>

      {/* ── UNCONTACTED LEADS LIST ────────────────────────────────────── */}
      {hasUrgentLeads && (
        <Card elevation={0} sx={{ mb: 2.5, borderRadius: 2, border: "1px solid #fecaca" }}>
          <CardContent sx={{ py: 2, px: 2, "&:last-child": { pb: 2 } }}>
            <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
              <LocalFireDepartmentIcon sx={{ color: "#ef4444", fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={800} color="#ef4444">
                New Leads Pending Contact
              </Typography>
              <Box sx={{ bgcolor: "#ef4444", color: "#fff", borderRadius: 10, px: 1, fontSize: 11, fontWeight: 700 }}>
                {newLeads.length}
              </Box>
            </Stack>
            {newLeads.map((lead) => (
              <UncontactedRow
                key={lead.id}
                lead={lead}
                onContacted={handleMarkContacted}
                onView={(id) => navigate(`/leads/${id}`)}
                showAssignee={inTeamScope}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── 1. OVERDUE ───────────────────────────────────────────────── */}
      {(data?.overdue?.length ?? 0) > 0 && (
        <>
          <SectionHeader
            icon={<WarningAmberIcon sx={{ color: "#ef4444", fontSize: 20 }} />}
            title="🚨 Overdue"
            count={data!.overdue.length}
            color="#ef4444"
            expanded={overdueOpen}
            onToggle={() => setOverdueOpen((p) => !p)}
          />
          <Collapse in={overdueOpen}>
            <Stack gap={1} mb={1}>
              {data!.overdue.map((t) => (
                <TaskCard key={t.id} task={t} onComplete={handleComplete} isOverdue onMarkLeadContacted={handleMarkContacted} onOpenDetail={setDetailTask} showAssignee={inTeamScope} />
              ))}
            </Stack>
          </Collapse>
        </>
      )}

      {/* ── 2. NEEDS ACTION TODAY ─────────────────────────────────────── */}
      <SectionHeader
        icon={<TodayIcon sx={{ color: "#3b82f6", fontSize: 20 }} />}
        title="🔥 Needs Action Today"
        count={data?.today?.length ?? 0}
        color="#3b82f6"
        expanded={todayOpen}
        onToggle={() => setTodayOpen((p) => !p)}
      />
      <Collapse in={todayOpen}>
        {(data?.today?.length ?? 0) > 0 ? (
          <Stack gap={1} mb={1}>
            {data!.today.map((t) => (
              <TaskCard key={t.id} task={t} onComplete={handleComplete} onMarkLeadContacted={handleMarkContacted} onOpenDetail={setDetailTask} showAssignee={inTeamScope} />
            ))}
          </Stack>
        ) : (
          <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e8e8e8", p: 2.5, textAlign: "center", mb: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 32, color: "#d1d5db", mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>All clear for today!</Typography>
          </Card>
        )}
      </Collapse>

      {/* ── 3. UPCOMING ──────────────────────────────────────────────── */}
      {(data?.upcoming?.length ?? 0) > 0 && (
        <>
          <SectionHeader
            icon={<HourglassEmptyIcon sx={{ color: "#10b981", fontSize: 20 }} />}
            title="⏳ Upcoming"
            count={data!.upcoming.length}
            color="#10b981"
            expanded={upcomingOpen}
            onToggle={() => setUpcomingOpen((p) => !p)}
          />
          <Collapse in={upcomingOpen}>
            <Stack gap={1}>
              {data!.upcoming.map((t) => (
                <TaskCard key={t.id} task={t} onComplete={handleComplete} onMarkLeadContacted={handleMarkContacted} onOpenDetail={setDetailTask} showAssignee={inTeamScope} />
              ))}
            </Stack>
          </Collapse>
        </>
      )}

      {/* All done state */}
      {stats.total === 0 && !hasUrgentLeads && (
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8e8e8", p: 5, textAlign: "center", mt: 2 }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: "#10b981", mb: 1 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={700}>All caught up!</Typography>
          <Typography variant="body2" color="text.secondary">No pending to-dos. Great work!</Typography>
        </Card>
      )}

      {/* ── CREATE TO-DO DIALOG ───────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); resetDialog(); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <AddIcon /> New To-Do
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} pt={0.5}>
            <TextField
              label="Title *"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth size="small"
              placeholder="e.g. Call Maria Garcia about wedding quote"
            />

            <PremiumDateTimePicker
              label="Due Date & Time *"
              value={dueAt}
              onChange={setDueAt}
            />

            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select value={priority} onChange={(e) => setPriority(e.target.value)} label="Priority">
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: v.color }} />
                      {v.label}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              options={leadOptions}
              loading={leadSearching}
              inputValue={leadSearchInput}
              onInputChange={handleLeadInputChange}
              value={linkedLead}
              onChange={(_, v) => setLinkedLead(v)}
              getOptionLabel={(o) => o.name}
              noOptionsText="Type to search leads..."
              renderInput={(params) => (
                <TextField {...params} label="Link to Lead (optional)" size="small"
                  InputProps={{ ...params.InputProps, endAdornment: (<>{leadSearching && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>) }} />
              )}
            />

            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth size="small" multiline rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => { setDialogOpen(false); resetDialog(); }} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!title.trim() || !dueAt || saving}
            sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1e3a5f" }, fontWeight: 700 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : "Create To-Do"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SNACKBAR ─────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
      <TaskDetailDialog
        open={!!detailTask}
        task={detailTask}
        teamUsers={teamUsers}
        onClose={() => setDetailTask(null)}
        onChanged={() => { loadData(); setDetailTask(null); }}
      />
    </Box>
  );
};

export default MyTasks;
